import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Spinner,
  Modal,
  ModalFooter,
} from '@orientamos/ui'
import { Plus, FileText, Search, Trash2, Eye, Edit, Building2 } from 'lucide-react'
import { listIntakes, deleteIntake, type IntakeListItem } from '@lib/api'
import { formatDate } from '@orientamos/shared'

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'complete', label: 'Completo' },
  { value: 'approved', label: 'Aprobado' },
]

const contractTypeOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'residential', label: 'Residencial' },
  { value: 'commercial', label: 'Comercial' },
]

const statusBadgeVariants: Record<string, 'default' | 'warning' | 'success' | 'primary'> = {
  draft: 'warning',
  complete: 'primary',
  approved: 'success',
}

const statusLabels: Record<string, string> = {
  draft: 'Borrador',
  complete: 'Completo',
  approved: 'Aprobado',
}

export default function IntakesPage() {
  const navigate = useNavigate()
  const [intakes, setIntakes] = useState<IntakeListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState('')

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [intakeToDelete, setIntakeToDelete] = useState<IntakeListItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadIntakes = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await listIntakes({
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        status: (statusFilter as 'draft' | 'complete' | 'approved') || undefined,
        contractType: (contractTypeFilter as 'residential' | 'commercial') || undefined,
      })

      if (result.success) {
        setIntakes(result.data)
        setPagination(result.pagination)
      } else {
        setError('Error al cargar las ordenes')
      }
    } catch {
      setError('Error de conexion')
    } finally {
      setIsLoading(false)
    }
  }, [pagination.page, pagination.limit, search, statusFilter, contractTypeFilter])

  useEffect(() => {
    loadIntakes()
  }, [loadIntakes])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const handleDelete = async () => {
    if (!intakeToDelete) return

    setIsDeleting(true)
    try {
      const result = await deleteIntake(intakeToDelete.id)
      if (result.success) {
        setIntakes((prev) => prev.filter((i) => i.id !== intakeToDelete.id))
        setDeleteModalOpen(false)
        setIntakeToDelete(null)
      }
    } catch (err) {
      console.error('Error deleting intake:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteModal = (intake: IntakeListItem) => {
    setIntakeToDelete(intake)
    setDeleteModalOpen(true)
  }

  return (
    <div>
      <PageHeader
        title="Ordenes de Contrato"
        description="Gestiona las ordenes de contrato y la informacion de clientes y propiedades"
        breadcrumbs={[{ label: 'Inicio', href: '/' }, { label: 'Ordenes de Contrato' }]}
        actions={
          <Link to="/intakes/new">
            <Button>
              <Plus className="w-4 h-4 mr-1" />
              Nueva Orden
            </Button>
          </Link>
        }
      />

      {/* Filters */}
      <Card className="mb-6" padding="sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Buscar por numero de orden o direccion..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="w-40">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            />
          </div>

          <div className="w-40">
            <Select
              options={contractTypeOptions}
              value={contractTypeFilter}
              onChange={(e) => {
                setContractTypeFilter(e.target.value)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            icon={<FileText className="w-12 h-12" />}
            title="Error al cargar"
            description={error}
            action={<Button onClick={loadIntakes}>Reintentar</Button>}
          />
        </Card>
      ) : intakes.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText className="w-12 h-12" />}
            title="Sin ordenes de contrato"
            description={
              search || statusFilter || contractTypeFilter
                ? 'No se encontraron ordenes con los filtros seleccionados'
                : 'Crea tu primera orden de contrato para comenzar a gestionar la informacion de clientes y propiedades'
            }
            action={
              search || statusFilter || contractTypeFilter ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('')
                    setStatusFilter('')
                    setContractTypeFilter('')
                  }}
                >
                  Limpiar filtros
                </Button>
              ) : (
                <Link to="/intakes/new">
                  <Button>
                    <Plus className="w-4 h-4 mr-1" />
                    Crear Primera Orden
                  </Button>
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <Table>
            <TableHead>
              <TableRow hoverable={false}>
                <TableHeader>Orden</TableHeader>
                <TableHeader>Inmueble</TableHeader>
                <TableHeader>Tipo</TableHeader>
                <TableHeader>Estado</TableHeader>
                <TableHeader>Fecha</TableHeader>
                <TableHeader className="w-32">Acciones</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {intakes.map((intake) => (
                <TableRow
                  key={intake.id}
                  onClick={() => navigate(`/intakes/${intake.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <div className="font-medium text-primary-600">{intake.orderNumber}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-neutral-400" />
                      <div>
                        <div className="text-sm">{intake.property.address}</div>
                        <div className="text-xs text-neutral-500">{intake.property.city}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={intake.contractType === 'residential' ? 'default' : 'secondary'}
                    >
                      {intake.contractType === 'residential' ? 'Residencial' : 'Comercial'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariants[intake.status]}>
                      {statusLabels[intake.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-neutral-500">{formatDate(intake.createdAt)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/intakes/${intake.id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/intakes/${intake.id}/edit`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(intake)}
                        className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200">
              <div className="text-sm text-neutral-500">
                Mostrando {(pagination.page - 1) * pagination.limit + 1} a{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total} ordenes
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eliminar Orden"
        description="Esta accion no se puede deshacer"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas eliminar la orden{' '}
          <span className="font-medium text-neutral-900">{intakeToDelete?.orderNumber}</span>? Se
          eliminaran todos los datos asociados.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
